import { league } from "../index.ts";
import { idb } from "../../db/index.ts";
import g from "../../util/g.ts";
import local from "../../util/local.ts";
import type { OwnerMood } from "../../../common/types.ts";
import type { OwnerEvaluation } from "../season/updateOwnerMood.ts";
import { COURT } from "../../../common/constants.ts";

// Describe a non-playoff team's draft positioning from its reverse-standings rank (0-based).
const draftOddsLabel = (oddsRank: number) => {
	const pick = oddsRank + 1;
	if (pick <= 1) return "the league's best draft odds";
	if (pick <= 3) return `top-${pick} draft odds`;
	if (pick <= 5) return "top-5 draft odds";
	return "a lottery pick";
};

const moodTexts = ["Horrible!", "Bad.", "Pretty good.", "Good.", "Excellent!"];

const getMoodScore = (total: number, deltas: boolean = false) => {
	if (total >= (deltas ? 0.5 : 2)) {
		return 4;
	}

	if (total >= (deltas ? 0.25 : 1)) {
		return 3;
	}

	if (total >= 0) {
		return 2;
	}

	if (total > (deltas ? -0.25 : -0.5)) {
		return 1;
	}

	return 0;
};

export const genMessage = async (
	deltas: OwnerMood,
	cappedDeltas: OwnerMood,
	evaluation?: OwnerEvaluation,
) => {
	// If auto play seasons or multi team mode, no messages - keep in sync with updateOwnerMood
	if (
		local.autoPlayUntil ||
		g.get("spectator") ||
		g.get("userTids").length > 1
	) {
		return;
	}

	// No need for seasons before you GMed this team or more than 10 years old, but also make sure we always have g.get("season")
	const minSeason = Math.min(
		g.get("season"),
		Math.max(g.get("gracePeriodEnd") - 2, g.get("season") - 9),
	);
	const teamSeasons = await idb.getCopies.teamSeasons(
		{
			tid: g.get("userTid"),
			seasons: [minSeason, g.get("season")],
		},
		"noCopyCache",
	);
	const moods = teamSeasons.map((ts) => {
		return (
			ts.ownerMood ?? {
				money: 0,
				playoffs: 0,
				wins: 0,
			}
		);
	});

	let m = "";
	let fired = false;

	// Check for some challenge modes that can result in being fired
	if (g.get("challengeFiredLuxuryTax")) {
		const latestSeason = teamSeasons.at(-1);
		if (latestSeason && latestSeason.expenses.luxuryTax > 0) {
			m +=
				'<p>You paid the luxury tax with the "You\'re fired if you pay the luxury tax" challenge mode enabled!</p>';
			fired = true;
		}
	}
	if (g.get("challengeFiredMissPlayoffs")) {
		const latestSeason = teamSeasons.at(-1);
		if (latestSeason && latestSeason.playoffRoundsWon < 0) {
			m +=
				'<p>You missed the playoffs with the "You\'re fired if you miss the playoffs" challenge mode enabled!</p>';
			fired = true;
		}
	}

	// If challenge mode didn't trigger a firing, do normal stuff
	if (!fired) {
		const currentMood = moods.at(-1) ?? {
			money: 0,
			playoffs: 0,
			wins: 0,
		};
		const currentTotal =
			currentMood.wins + currentMood.playoffs + currentMood.money;
		fired =
			currentTotal <= -1 &&
			g.get("season") >= g.get("gracePeriodEnd") &&
			!g.get("godMode");

		if (!fired) {
			let overall;

			if (g.get("season") < g.get("gracePeriodEnd")) {
				overall = "It's too early to judge you.";
			} else if (g.get("godMode")) {
				overall = "You're using God Mode, so who cares what I think?";
			} else {
				overall = moodTexts[getMoodScore(currentTotal)];
			}

			const deltasTotal = deltas.wins + deltas.playoffs + deltas.money;
			const deltasMoodScore = getMoodScore(deltasTotal, true);
			const cappedDeltasTotal =
				cappedDeltas.wins + cappedDeltas.playoffs + cappedDeltas.money;
			const cappedDeltasMoodScore = getMoodScore(cappedDeltasTotal, true); // Average the capped and uncapped scores, unless perfect

			const avgMoodScore =
				currentTotal >= 2.99
					? deltasMoodScore
					: Math.round((deltasMoodScore + cappedDeltasMoodScore) / 2);
			const thisYear = moodTexts[avgMoodScore];
			let text;

			// Report-Card-style, draft-odds-first framing takes priority: a title is the pinnacle,
			// and a genuine rebuild is judged on the draft odds it secured, not the losing.
			const strongRebuild =
				!!evaluation &&
				!evaluation.madePlayoffs &&
				evaluation.oddsQuality >= 0.5;

			if (evaluation?.champion) {
				text =
					"A championship — this is exactly what I hired you for. Outstanding work.";
			} else if (strongRebuild) {
				text = `I know the record stings, but you're clearly building for the future — locking up ${draftOddsLabel(
					evaluation!.oddsRank,
				)} is the plan working. Keep developing the young core.`;
				if (deltas.money < 0) {
					text += " Just keep an eye on the books.";
				}
			} else if (currentTotal >= 0) {
				if (deltas.playoffs >= 0 && deltas.wins >= 0) {
					if (deltas.money < 0) {
						text = `Keep it up on the ${COURT}, but I need more money.`;
					} else {
						text = "Keep it up.";
					}
				} else {
					text = "Hopefully you'll win some more games next season.";

					if (deltas.money < 0) {
						text += " And try to make some more money too!";
					}
				}
			} else if (
				deltas.playoffs >= 0 &&
				deltas.wins >= 0 &&
				deltas.money >= 0
			) {
				text = "Keep it up.";
			} else if (deltas.playoffs < 0 && deltas.money < 0) {
				text = "Somehow you need to win more and make more money.";
			} else if (deltas.playoffs < 0) {
				text = "Win some more games next season.";
			} else if (deltas.money < 0) {
				text = "Be careful about losing too much money.";
			}

			if (g.get("season") >= g.get("gracePeriodEnd")) {
				if (currentTotal + deltas.money + deltas.playoffs + deltas.wins < -1) {
					text = "Another season like that and you're fired!";
				} else if (
					currentTotal + 2 * (deltas.money + deltas.playoffs + deltas.wins) <
					-1
				) {
					text = "A couple more seasons like that and you're fired!";
				}
			}

			m += `<p>This year: ${thisYear}</p><p>Overall: ${overall}</p>`;

			if (text) {
				m += `<p>${text}</p>`;
			}

			// Summer League is a single-franchise game: the user is permanently tied to the
			// team they chose, so other teams never poach them. Keep otherTeamsWantToHire
			// pinned false (rather than the upstream success-weighted random job offer).
			await league.setGameAttributes({
				otherTeamsWantToHire: false,
			});
		} else {
			// Fired!
			if (
				currentMood.wins < 0 &&
				currentMood.playoffs < 0 &&
				currentMood.money < 0
			) {
				m += "<p>You've been an all-around disappointment. You're fired.</p>";
			} else if (
				currentMood.money < 0 &&
				currentMood.wins >= 0 &&
				currentMood.playoffs >= 0
			) {
				m +=
					"<p>You've won some games, but you're just not making me enough profit. It's not all about wins and losses, dollars matter too. You're fired.</p>";
			} else if (
				currentMood.money >= 0 &&
				currentMood.wins < 0 &&
				currentMood.playoffs < 0
			) {
				m += `<p>I like that you've made a nice profit for me, but you're not putting a competitive team on the ${COURT}. We need a new direction. You're fired.</p>`;
			} else {
				m += "<p>You're fired.</p>";
			}
		}
	}

	if (fired) {
		// Single-franchise game: no "other teams are hiring / take a look" job-hunt link. The
		// branded fired screen (GmMessage.tsx) offers only "Start New League".
		await league.setGameAttributes({
			gameOver: true,
		});
	}

	await idb.cache.messages.add({
		read: false,
		from: "The Owner",
		year: g.get("season"),
		text: m,
		subject: "Annual performance evaluation",
		tid: g.get("userTid"),
		ownerMoods: moods,
	});
};
